import { isAndroid, isIOS, isSuperAcme } from '@/utils/env';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Box, IconButton, LinearProgress, Toolbar, Typography } from '@mui/material';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { history, useIntl } from 'umi';
import styles from './index.less';

import Loading from '@/components/Layout/components/Loading';
import ExclamationCircleIcon from '@mui/icons-material/Warning';
import { Avatar } from '@mui/material';
import CardButton from '../CardButton';

/**
 * 安全区域-解决方案
 * 1. 优先级最高，从search参数中尝试获取statusBarTop，如果有定义且能转换为数字，则生效
 *    IOS端在入口连接中会带入statusBarTop参数作为顶部安全区域的正确值
 *  （历史问题，IOS的Webview在顶部隐藏了个导航栏，导致通过样式safe-area-inset-top获取的值非正确）
 * 2. 次优先级，从userAgent中获取4个方向的安全区域，有拿到值则生效
 * 3. 兜底方案，IOS采用CSS的fix顶底安全高度，Android采用固定值40的顶部高度
 */
const urlParams = new URLSearchParams(location.search);
const statusBarTop = urlParams.get('statusBarTop') || ''; // IOS会带入statusBarTop
const statusBarBottom = urlParams.get('statusBarBottom') || ''; // IOS会带入statusBarTop
let safeTop: number | undefined = parseInt(statusBarTop);
let safeBottom: number | undefined = parseInt(statusBarBottom);
if (isNaN(safeTop)) {
  safeTop = undefined;
}
if (isNaN(safeBottom)) {
  safeBottom = undefined;
}
if (safeTop === undefined) {
  const { userAgent } = window.navigator;
  const statusBarTopMatch = userAgent.match(/statusBarTop\s+([\d.]+)/i);
  if (statusBarTopMatch) {
    safeTop = parseInt(statusBarTopMatch[1]); // 将得到例如 "30.1223"
    if (isNaN(safeTop)) {
      safeTop = undefined;
    }
  } else {
    safeTop = undefined;
  }
}
if (safeBottom === undefined) {
  const { userAgent } = window.navigator;
  const statusBarBottomMatch = userAgent.match(/statusBarTop\s+([\d.]+)/i);
  if (statusBarBottomMatch) {
    safeBottom = parseInt(statusBarBottomMatch[1]); // 将得到例如 "30.1223"
    if (isNaN(safeBottom)) {
      safeBottom = undefined;
    }
  } else {
    safeBottom = undefined;
  }
}
// 为了安卓侧的兼容，原来老的版本安卓App侧不支持顶部安全区域，写死了一个值
if (isAndroid && safeTop === undefined) {
  safeTop = 40;
}
if (isAndroid && safeBottom === undefined) {
  safeBottom = 0;
}

/**
 * 一些兼容原来顶部导航的样式
 */
let topBkColor = '#f3f3fd';
let topNvgBkColor = 'transparent';
let topNvgColor = '#333';
let topTitleSX = {
  fontWeight: 'bold',
  color: '#222222',
  fontSize: '16px',
};
if (BRAND === 'sensforge') {
  topBkColor = '#fff';
  topNvgBkColor = '#fafafa';
  topNvgColor = '#979797';
  topTitleSX = {
    fontWeight: 'normal',
    color: '#000000',
    fontSize: '14px',
  };
}

/**
 * 定义
 */
interface IProps {
  /**
   * 子内容
   */
  children?: ReactNode;
  /**
   * 样式类
   */
  bodyClassName?: string;

  /**
   * 浏览器标题栏区域标题
   */
  webTitle?: string;
  /**
   * 导航栏标题
   */
  barTitle?: ReactNode;
  /**
   * 是否展示返回按钮
   */
  barShowBack?: boolean;
  /**
   * 返回按钮自定义事件
   */
  barLeftClick?: () => void;
  /**
   * 右侧更多功能
   */
  barMore?: ReactNode;

  /**
   * 顶部容器区域
   */
  head?: ReactNode;
  /**
   * 中间主体容器区域
   */
  body?: ReactNode;
  /**
   * 底部容器区域
   */
  foot?: ReactNode;
  /**
   * 内容区域是否容器滚动布局
   * 默认为true
   */
  bodyScroll?: boolean;
  /**
   * 当前是否正在处理
   */
  doing?: boolean;
  /**
   * 当前加载中
   */
  loading?: boolean;
  /**
   * 当前存在错误
   */
  error?: boolean;
  /**
   * 背景颜色，不同的业务场景背景色可能不同
   * - 如果没有标题，那么顶部的安全区域背景色会跟随背景颜色
   */
  backgroundColor?: string;
  /**
   * 标题栏背景颜色，同顶部的安全区域的背景颜色
   */
  titleBackgroundColor?: string;
  /**
   * 如果有输入框的页面，可以开启该功能，能保证导航条在键盘弹出的时候也能顶部固定
   */
  fixHeaderPosition?: boolean;
}
/**
 * 卡片大容器
 * 分为顶部容器/中间容器/底部容器
 * - 解决各种浏览器容器环境的高度兼容问题
 * - 整合了通用标题栏
 * - 不兼容Layout组件的headerNode属性，该属性采用fix布局的定位
 * 主要解决容器外边距的问题以及上下安全区域
 * ScreenContainer
 */
export default (props: IProps) => {
  const intl = useIntl();
  const {
    webTitle,
    barTitle,
    barShowBack = true,
    barLeftClick,
    barMore,
    head,
    body,
    foot,
    bodyScroll = true,
    doing,
    bodyClassName,
    loading,
    error,
    backgroundColor,
    titleBackgroundColor,
    fixHeaderPosition,
  } = props;

  // 如下代码解决键盘弹出来后，IOS整个容器被上推，导致的标题也被上推的问题，这里会把标题拉回来到容器可见区域的顶部
  const refBody = useRef<HTMLElement>(null);
  const refHead = useRef<HTMLElement>(null);
  const timerIdRef = useRef<number | null>(null);

  const timeoutRef = useRef<number | null>(null);
  const currentTopRef = useRef(0);
  const currentHeightRef = useRef(window.innerHeight);
  useEffect(() => {
    if (!fixHeaderPosition) {
      return;
    }
    const innerHeight = window.innerHeight;
    console.log('innerHeight:', innerHeight);

    // 动画方法，保证了每次在主线程只有一次在执行
    const fixTop = () => {
      if (!refHead.current || !refBody.current) {
        return;
      }
      try {
        const info = refBody.current.getBoundingClientRect();
        // info.top发生变化说明导航被滚动，且当前因为键盘导致视图减小至少100像素（键盘通常比该值要高），则需要调整导航位置
        // 因为info.top发生变化不一定视图上展示的容器真被键盘推上去，有可能是IOS的背景回弹机制（视图还在原地，但取到的偏移值是回弹的值）
        if (currentTopRef.current !== info.top) {
          // 当发现不同的时候，通常IOS侧只触发一次值改变
          currentTopRef.current = info.top;
          console.log('screenSize:', currentTopRef.current, window.innerHeight);
          // 键盘弹出，window.innerHeight的高度为原始高度-被顶上去的高度（注意不一定是减去键盘高度，因为顶上去后还是可以通过滑动把视图滑下来）
          if (window.innerHeight < innerHeight) {
            // 小于说明可能键盘弹出，或则会其他情况导致视图被滑上去了
            if (info.top < 0) {
              refHead.current.style.transform = `translateY(${-info.top}px)`;
              // if (currentHeightRef.current !== window.innerHeight) {
              //   console.log('screenSize cubic-bezier');
              //   refHead.current.style.transition = `transform 0.4s cubic-bezier(0.1, 0.7, 0.3, 1)`; // 先快后慢的物理感
              // } else {
              refHead.current.style.transition = `transform 0s`;
              //}
            } else {
              refHead.current.style.transform = `translateY(0px)`;
              refHead.current.style.transition = `transform 0s`;
            }
          }
          // 键盘收回
          else {
            refHead.current.style.transform = `translateY(0px)`;
            refHead.current.style.transition = `transform 0s`;
          }
          currentHeightRef.current = window.innerHeight; // 同步一下当前视图高度，通常如果键盘出来了再进行滚动导致的info.top变化，就不需要动态慢慢偏移了
        }
      } catch (ex: any) {
        console.log(ex.message);
      }
      // 下一个动画周期，这里降低下频率，提高性能
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        timerIdRef.current = requestAnimationFrame(fixTop);
      }, 100);
    };
    timerIdRef.current = requestAnimationFrame(fixTop);
    return () => {
      if (timerIdRef.current) {
        cancelAnimationFrame(timerIdRef.current);
        timerIdRef.current = null;
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // 移动端webview安全区域
  useEffect(() => {
    if (isIOS) {
    }
    if (isAndroid) {
    }
  }, []);

  // 标题
  useEffect(() => {
    document.title = webTitle || '';
  }, [webTitle]);

  // 安卓和IOS分别解决
  const sp = '14px';

  // 事件逻辑-返回按钮
  const handleBack = useCallback(() => {
    if (barLeftClick) {
      barLeftClick();
      return;
    }
    try {
      if (!isSuperAcme) {
        history.back();
      } else {
        window.SuperAcme.call('navigateBack');
      }
    } catch (error) {
      console.log('error=====', error);
    }
  }, [barLeftClick, isSuperAcme]);

  // 主内容区域根据情况是否正在加载、是否存在错误、是否为空进行渲染
  let content = null;
  // 存在错误的情况，Layout组件中封装的错误展示有个问题，全屏模式会挡住上方的导航栏，另外错误按钮颜色写死了
  if (error) {
    const fetchDataFailed = intl.formatMessage({ id: 'fetchDataFailed' });
    const checkNetworkAndReload = intl.formatMessage({ id: 'checkNetworkAndReload' });
    const txttapToRetry = intl.formatMessage({ id: 'tapToRetry' });
    content = (
      <Box
        sx={{
          padding: '20px',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Avatar
          sx={{ backgroundColor: '#eaeaea', width: '100px', height: '100px', marginTop: '-70px' }}
        >
          <ExclamationCircleIcon fontSize="large" sx={{ color: 'white' }} />
        </Avatar>
        <Typography
          sx={{
            fontSize: '14px',
            marginTop: '20px',
          }}
        >
          {fetchDataFailed}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            marginBottom: '20px',
            opacity: 0.6,
          }}
        >
          {checkNetworkAndReload}
        </Typography>
        <CardButton
          title={txttapToRetry}
          color="primary"
          onClick={() => {
            // 这里需要额外处理，reload基于当前页面，而单页项目使用history.push进行跳转后，可能已经丢失了statusBarTop等参数，这里需要补充回来
            const url = new URL(window.location.href);
            if (!url.searchParams.has('statusBarTop')) {
              url.searchParams.append('statusBarTop', statusBarTop); // 设置默认值或其他需要的值
              console.log('reload from ', location.href, ' to ', url.href);
              window.location.replace(url.href);
            } else {
              window.location.reload();
            }
          }}
        ></CardButton>
      </Box>
    );
  }
  // 正在加载的情况
  else if (loading) {
    const loadingText = intl.formatMessage({ id: 'loading' });
    content = <Loading mode="refresh" text={`${loadingText}...`} />;
  }
  // 正常情况
  else {
    const contentFixTop = (
      <Box
        className={safeTop !== undefined ? '' : styles.fixPt}
        sx={{ paddingTop: `${safeTop !== undefined ? `${safeTop}px` : ''}` }}
      ></Box>
    );
    const contentFixBottom = (
      <Box
        className={safeBottom !== undefined ? '' : styles.fixPb}
        sx={{ paddingBottom: `${safeBottom !== undefined ? `${safeBottom}px` : ''}` }}
      ></Box>
    );
    content = (
      <>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: bodyScroll ? 'auto' : 'hidden',
            padding: `${sp}`,
          }}
          className={bodyClassName}
        >
          {/** 顶部容器不存在的情况下这里要fix移动端安全区域 */}
          {head || barTitle ? '' : contentFixTop}
          {/* 子内容 */}
          {props.children}
          {body}
        </Box>
        {/* 底部容器 */}
        {foot ? (
          <Box
            sx={{
              padding: sp,
              background: '#fff',
              flexShrink: 0,
            }}
          >
            {foot}
            {contentFixBottom}
          </Box>
        ) : (
          <>{contentFixBottom}</>
        )}
      </>
    );
  }

  // 渲染
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: backgroundColor || '#f8f8f8',
        position: 'fixed', // 注意，错误用法 height: '100vh', 在例如Safari浏览器，浏览器导航栏在底部，100vh包括了浏览器导航栏底部遮挡住的区域
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      ref={refBody}
    >
      {/* 顶部容器 */}
      {head || barTitle ? (
        <>
          <Box
            className={safeTop !== undefined ? '' : styles.fixPt}
            sx={{
              flexShrink: 0,
              backgroundColor: titleBackgroundColor || topBkColor,
              paddingTop: `${safeTop !== undefined ? `${safeTop}px` : ''}`,
              position: 'relative',
              zIndex: '999999',
              transition: 'transform 0.3s',
            }}
            ref={refHead}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '-100px',
                left: '0px',
                right: '0px',
                height: '100px',
                zIndex: '999999',
                backgroundColor: titleBackgroundColor || topBkColor,
              }}
            ></Box>
            {/** 导航栏 */}
            {barTitle ? (
              <Box>
                <Toolbar
                  sx={{
                    minHeight: '24px!important',
                    padding: `${sp}`,
                  }}
                >
                  {/** 这里IOS的返回按钮位置和原生对齐，看看效果 */}
                  {barShowBack ? (
                    <IconButton
                      sx={{
                        position: 'absolute',
                        padding: '8px',
                        left: '4px',
                        top: '3px',
                        backgroundColor: topNvgBkColor,
                        color: topNvgColor,
                      }}
                      onClick={handleBack}
                    >
                      <NavigateBeforeIcon sx={{ fontSize: '28px' }} />
                    </IconButton>
                  ) : (
                    ''
                  )}
                  <Box sx={{ flexGrow: 1, margin: '0 30px' }}>
                    <Typography textAlign="center" sx={topTitleSX}>
                      {barTitle}
                    </Typography>
                  </Box>
                  {barMore ? <Box>{barMore}</Box> : <></>}
                </Toolbar>
              </Box>
            ) : (
              ''
            )}
            {/* 顶部容器 */}
            {head}
          </Box>
        </>
      ) : (
        ''
      )}
      {/* 处理中进度条 */}
      {doing ? (
        <Box sx={{ height: 0, position: 'relative' }}>
          <LinearProgress
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
            }}
          />
        </Box>
      ) : null}
      {/* 主内容区 */}
      {content}
    </Box>
  );
};
